import type {
  FieldsTypes,
  SearchItem,
  SearchResult,
  TypedField,
} from "~/models/Content/SearchResult"
import type {
  BaseSearchResponse,
  LocationHit,
} from "~/models/Content/Response"
import {
  formatAuthorField,
  formatBinaryFileField,
  formatBooleanField,
  formatDateField,
  formatImageAssetField,
  formatImageField,
  formatIntegerField,
  formatMatrixField,
  formatMetadataField,
  formatObjectRelationField,
  formatRichTextField,
  formatSelectionField,
  formatStringField,
  formatTagsField,
  formatTextField,
  formatUrlField,
} from "./formatFieldValues"
import type { ContentType } from "../models/Content/ContentType"
import { formatAggregations } from "./formatAggregations"

/**
 * Formats the search results from the API response into the application's SearchResult model
 * @param result The API search response
 * @param imageBaseUrl - Base URL for images, used to format image asset fields
 * @returns Formatted SearchResult object including items and aggregations
 */
export const formatSearchResults = (
  result: BaseSearchResponse,
  imageBaseUrl: string,
): SearchResult => {
  if (!result?.View?.Result?.searchHits?.searchHit) {
    return {
      count: 0,
      items: [],
      relations: null,
    }
  }

  return {
    count: result.View.Result.count,
    items:
      result.View.Result.searchHits.searchHit.map(formatSearchItem(imageBaseUrl)),
    relations: null,
    aggregations: formatAggregations(result.View.Result.aggregations),
  }
}

const formatSearchItem = (imageBaseUrl: string) => (hit: LocationHit): SearchItem => {
  const content = hit.value.Location.ContentInfo.Content
  const version = content?.CurrentVersion?.Version ?? {}
  const contentType =
    hit.value.Location?.ContentInfo?.ContentType?._identifier

  if (!version || !contentType) {
    throw new Error("Missing required content data")
  }

  const fields: Record<string, TypedField> = {}
  version.Fields.field.forEach(field => {
    fields[field.fieldDefinitionIdentifier] = formatFieldValue(
      field.fieldTypeIdentifier as keyof FieldsTypes,
      field.fieldValue,
      imageBaseUrl,
      contentType,
      field.fieldDefinitionIdentifier,
    )
  })

  const relationIds =
    version.Relations?.Relation?.map(relation => {
      const href = relation.DestinationContent._href
      // Extract the ID from the end of the href path "/api/ezp/v2/content/objects/7073"
      return Number(href.split("/").pop())
    }) ?? []

  return {
    contentType,
    id: content._id,
    fields,
    relationIds,
    metadata: {
      locationId: hit.value.Location.id,
      pathString: hit.value.Location.pathString,
      sortField: hit.value.Location.sortField,
      sortOrder: hit.value.Location.sortOrder,
      published: content.publishedDate,
      modified: version.VersionInfo?.modificationDate || "",
      versionNo: version.VersionInfo?.versionNo || 0,
      remoteId: content._remoteId,
      parentLocationId: hit.value.Location.ParentLocation
        ? Number(
            hit.value.Location.ParentLocation._href.split("/").pop(),
          )
        : null,
      mainLocationId: content.MainLocation
        ? Number(content.MainLocation._href.split("/").pop())
        : 0,
      priority: hit.value.Location.priority || 0,
      childCount: hit.value.Location.childCount || 0,
    },
  }
}

const formatFieldValue = (
  fieldType: keyof FieldsTypes,
  rawValue: unknown,
  imageBaseUrl: string,
  contentType?: ContentType,
  fieldKey?: string,
): TypedField => {
  let value
  switch (fieldType) {
    case "ezstring":
      value = formatStringField(rawValue)
      break
    case "eztext":
      value = formatTextField(rawValue)
      break
    case "ezrichtext":
      value = formatRichTextField(rawValue)
      break
    case "ezimage":
      value = formatImageField(rawValue)
      break
    case "ezimageasset":
      value = formatImageAssetField(rawValue, imageBaseUrl)
      break
    case "ezobjectrelation":
    case "ezobjectrelationlist":
      value = formatObjectRelationField(rawValue)
      break
    case "eztags":
      value = formatTagsField(rawValue)
      break
    case "ezboolean":
      value = formatBooleanField(rawValue)
      break
    case "ezauthor":
      value = formatAuthorField(rawValue)
      break
    case "ezselection":
      value = formatSelectionField(
        rawValue,
        contentType || "",
        fieldKey || "",
      )
      break
    case "ezmatrix":
      value = formatMatrixField(rawValue)
      break
    case "metadata":
      value = formatMetadataField(rawValue)
      break
    case "ezinteger":
      value = formatIntegerField(rawValue)
      break
    case "ezdate":
      value = formatDateField(rawValue)
      break
    case "ezurl":
      value = formatUrlField(rawValue)
      break
    case "ezbinaryfile":
      value = formatBinaryFileField(rawValue)
      break
    default:
      value = rawValue || ""
      break
  }

  return {
    type: fieldType,
    value,
    rawValue: String(rawValue),
  }
}
